import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  Tooltip,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  TablePagination,
  Grid,
  Menu,
  CircularProgress,
  MenuItem,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import {
  useLazyGetEmailCampaignReportQuery,
  useGetEmailLogsQuery,
} from "../store/services/emailApi";
import { layoutCSS, cellSX, tableHeaderSX } from "../component/common/commonStyle";
import { useNavigate } from "react-router-dom";

// ── Status mapping (API file_status → UI status) ────────────────────────────────
const mapApiStatus = (status) => {
  if (!status) return "DRAFT";
  const s = status.toLowerCase();
  if (s === "processed" || s === "sent") return "SENT";
  if (s === "pending") return "PENDING";
  if (s === "failed" || s === "error") return "FAILED";
  return "DRAFT";
};

const STATUS_COLORS = {
  SENT: { bg: "#dcfce7", color: "#15803d" },
  DRAFT: { bg: "#f1f5f9", color: "#475569" },
  PENDING: { bg: "#fef3c7", color: "#b45309" },
  FAILED: { bg: "#fee2e2", color: "#b91c1c" },
};

const TAB_FILTERS = ["ALL", "SENT", "DRAFT", "PENDING", "FAILED"];

const ALL_COLUMNS = [
  { key: "templateName", label: "Template" },
  { key: "emailType", label: "Type" },
  { key: "logID", label: "Log ID" },
  { key: "sentOn", label: "Sent On" },
  { key: "status", label: "Status" },
  { key: "actions", label: "Actions" },
];

const glassSx = {
  background: "rgba(255, 255, 255, 0.7)",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255, 255, 255, 0.3)",
  boxShadow: "0 4px 12px 0 rgba(31, 38, 135, 0.05)",
};

// ── Shared glass field style (mirrors the delivery report) ──
const inputSx = {
  borderRadius: "0.5rem",
  minWidth: 100,
  "& .MuiOutlinedInput-root": {
    background: "rgba(255,255,255,0.7)",
    "& fieldset": { borderColor: "#E2E8F0" },
    "&:hover fieldset": { borderColor: "#94A3B8" },
  },
  "& .MuiInputLabel-root": { color: "#475569" },
  "& .Mui-disabled": {
    "& .MuiOutlinedInput-input": {
      color: "#334155 !important",
      WebkitTextFillColor: "#334155 !important",
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#CBD5E1" },
  },
};

const EmailCampaignReport = () => {
  const navigate = useNavigate();

  // Date pickers default to today
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());

  // Lazy query – data fetched only on trigger call
  const [trigger, { data: apiResponse, isLoading, error, refetch: refetchCampains }] =
    useLazyGetEmailCampaignReportQuery();

  // Transform API data to UI format
  const campaigns = useMemo(() => {
    if (!apiResponse?.data) return [];
    return apiResponse.data.map((log) => {
      const uiStatus = mapApiStatus(log.file_status);
      return {
        id: log.email_log_id,
        name: log.email_subject || log.email_template_name || "Untitled",
        templateName: log.email_template_name || "N/A",
        logID: log.email_log_id,
        emailType: log.email_type || "N/A",
        status: uiStatus,
        // Prefer `time_to_send` when available; fallback to `createddate`
        sentOn: (log.time_to_send || log.createddate)
          ? new Date(log.time_to_send || log.createddate).toLocaleString("en-US", {
            month: "short",
            day: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })
          : null,
        published: 0,
        sent: 0,
        delivered: 0,
        openedRead: 0,
        clicked: 0,
        raw: log,
      };
    });
  }, [apiResponse]);

  // Tab
  const [tabValue, setTabValue] = useState(0);

  // Search text (local filtering)
  const [searchQuery, setSearchQuery] = useState("");

  // Selection
  const [selected, setSelected] = useState([]);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Column visibility settings
  const [settingsAnchor, setSettingsAnchor] = useState(null);
  const [visibleCols, setVisibleCols] = useState(ALL_COLUMNS.map((c) => c.key));

  // Log detail dialog
  const [selectedLog, setSelectedLog] = useState(null);
  const {
    data: emailLogs,
    isLoading: emailLogsLoading,
    error: emailLogsError,
    refetch: emailLogsRefetch,
  } = useGetEmailLogsQuery(selectedLog?.id, { skip: !selectedLog?.id });

  // Last refreshed
  const [lastRefreshed, setLastRefreshed] = useState(() =>
    new Date().toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  );

  // ── Derived filtered data ──────────────────────────────────────────────────
  const tabFilter = TAB_FILTERS[tabValue];

  const filtered = useMemo(() => {
    let data = campaigns;
    if (tabFilter !== "ALL") data = data.filter((c) => c.status === tabFilter);
    if (searchQuery.trim())
      data = data.filter(
        (c) =>
          c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          String(c.id).includes(searchQuery)
      );
    // Date range filtering (already done via API, but extra safety)
    if (startDate && endDate) {
      const start = startDate.startOf("day").valueOf();
      const end = endDate.endOf("day").valueOf();
      data = data.filter((c) => {
        const dateStr = c.raw.time_to_send || c.raw.createddate;
        if (!dateStr) return true;
        const cDate = new Date(dateStr).getTime();
        return cDate >= start && cDate <= end;
      });
    }
    return data;
  }, [campaigns, tabFilter, searchQuery, startDate, endDate]);

  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage]
  );

  // 🟢 Tab counts – use API statusCounts when available
  const tabCounts = useMemo(() => {
    if (apiResponse?.statusCounts) {
      const { sent, failed, pending, total } = apiResponse.statusCounts;
      const draft = total - (sent + failed + pending);
      return TAB_FILTERS.map((f) => {
        switch (f) {
          case "ALL":
            return total;
          case "SENT":
            return sent;
          case "FAILED":
            return failed;
          case "PENDING":
            return pending;
          case "DRAFT":
            return draft;
          default:
            return 0;
        }
      });
    }
    // fallback to local calculation
    return TAB_FILTERS.map((f) =>
      f === "ALL"
        ? campaigns.length
        : campaigns.filter((c) => c.status === f).length
    );
  }, [campaigns, apiResponse?.statusCounts]);

  // ── Moved before early returns to avoid conditional hooks ──────────────────
  const hasData = apiResponse !== undefined;
  const apiCounts = apiResponse?.statusCounts;

  const summaryText = useMemo(() => {
    if (apiCounts) {
      return `TOTAL: ${apiCounts.total} | SENT: ${apiCounts.sent} | FAILED: ${apiCounts.failed} | PENDING: ${apiCounts.pending}`;
    }
    return `TOTAL: ${filtered.length} | SENT: ${filtered.filter((c) => c.status === "SENT").length
      } | FAILED: ${filtered.filter((c) => c.status === "FAILED").length
      } | PENDING: ${filtered.filter((c) => c.status === "PENDING").length
      }`;
  }, [apiCounts, filtered]);

  // ── Selection helpers ──────────────────────────────────────────────────────
  const allPageSelected =
    paginated.length > 0 && paginated.every((r) => selected.includes(r.id));
  const somePageSelected =
    paginated.some((r) => selected.includes(r.id)) && !allPageSelected;

  const toggleAll = () => {
    if (allPageSelected)
      setSelected((prev) =>
        prev.filter((id) => !paginated.find((r) => r.id === id))
      );
    else
      setSelected((prev) => [
        ...new Set([...prev, ...paginated.map((r) => r.id)]),
      ]);
  };

  const toggleRow = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  // ── Download CSV ───────────────────────────────────────────────────────────
  const handleDownload = () => {
    const rows =
      selected.length > 0
        ? campaigns.filter((c) => selected.includes(c.id))
        : filtered;
    const headers = [
      "ID",
      "Subject",
      "Template",
      "Status",
      "Sent On",
      "Published",
      "Sent",
      "Delivered",
      "Opened/Read",
      "Clicked",
    ];
    const csv = [
      headers.join(","),
      ...rows.map((r) =>
        [
          r.id,
          r.name,
          r.templateName,
          r.status,
          r.sentOn || "NA",
          r.published,
          r.sent,
          r.delivered,
          r.openedRead,
          r.clicked,
        ].join(",")
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "campaigns.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Search button handler ──────────────────────────────────────────────────
  const handleSearch = () => {
    trigger({
      start_date: startDate.format("YYYY-MM-DD"),
      end_date: endDate.format("YYYY-MM-DD"),
    });
  };

  const handleRefresh = () => {
    refetchCampains();
    setLastRefreshed(
      new Date().toLocaleString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    );
  };

  const toggleCol = (key) =>
    setVisibleCols((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );

  const tabLabel = (label, count) =>
    count > 0 ? `${label} (${count})` : label;

  // ── Loading state ──────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <Box
        sx={{
          p: 2.5,
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Skeleton variant="text" width={200} height={40} />
          <Skeleton variant="rectangular" width={100} height={36} />
        </Box>
        <Skeleton
          variant="rectangular"
          height={400}
          sx={{ borderRadius: 3, ...glassSx }}
        />
      </Box>
    );
  }

  // ── Error state ────────────────────────────────────────────────────────────
  if (error) {
    return (
      <Box
        sx={{
          p: 2.5,
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        }}
      >
        <Alert severity="error" sx={{ ...glassSx, borderRadius: 2 }}>
          Failed to load campaign logs: {error?.data?.message || error?.error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={layoutCSS}>
      <Typography
        variant="h6"
        sx={{ mb: 1, fontSize: "0.9rem", fontWeight: "bold" }}
      >
        Email Campaigns
      </Typography>

      {/* ── Glass filter bar (mirrors SMS report page) ── */}
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          alignItems: "center",
          p: 2,
          mb: 2,
          borderRadius: "0.5rem",
          backgroundColor: "#ffffff52",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 12px rgba(14,165,233,0.1)",
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={(newValue) => setStartDate(newValue)}
            format="DD/MM/YYYY"
            sx={{ ...inputSx, width: 140 }}
            slotProps={{ textField: { size: "small" } }}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={(newValue) => setEndDate(newValue)}
            minDate={startDate}
            format="DD/MM/YYYY"
            sx={{ ...inputSx, width: 140 }}
            slotProps={{ textField: { size: "small" } }}
          />
        </LocalizationProvider>
          <TextField
          label="Search Campaigns"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(0);
          }}
          size="small"
          sx={{ ...inputSx, width: 180 }}
        />
        <Button
          variant="contained"
          onClick={handleSearch}
          disabled={isLoading}
          startIcon={<SearchIcon fontSize="small" />}
          sx={{
            textTransform: "none",
            fontSize: "0.75rem",
            bgcolor: "#0288d1",
            "&:hover": { bgcolor: "#0277bd" },
            minWidth: 80,
          }}
        >
          Search
        </Button>

      </Paper>

      {/* Campaign status summary – now using API counts */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ fontSize: "0.75rem", mt: 0.5 }}>
          {summaryText}
        </Typography>
      </Box>

      {/* Main Paper */}
      <Paper sx={{ ...glassSx, borderRadius: "16px", overflow: "hidden" }}>
        {/* Tabs + Actions */}
        <Box
          sx={{
            px: 2,
            pt: 1,
            display: "flex",
            alignItems: "center",
            borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
          }}
        >
          <Tabs
            value={tabValue}
            onChange={(_, v) => {
              setTabValue(v);
              setPage(0);
              setSelected([]);
            }}
            variant="scrollable"
            scrollButtons={false}
            sx={{
              minHeight: 48,
              "& .MuiTab-root": {
                textTransform: "none",
                fontSize: "0.85rem",
                fontWeight: 600,
                minWidth: "auto",
                px: 2.5,
                color: "#64748b",
                py: 1.5,
                transition: "all 0.2s",
                "&:hover": { color: "#1a203e" },
              },
              "& .Mui-selected": { color: "#1a203e !important" },
              "& .MuiTabs-indicator": {
                backgroundColor: "#1a203e",
                height: 3,
                borderRadius: "3px 3px 0 0",
              },
            }}
          >
            {TAB_FILTERS.map((label, idx) => (
              <Tab
                key={label}
                label={tabLabel(
                  label.charAt(0) + label.slice(1).toLowerCase(),
                  tabCounts[idx]
                )}
              />
            ))}
          </Tabs>

          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1.5 }}>
            <Tooltip title="Export Data">
              <IconButton
                size="small"
                onClick={handleDownload}
                sx={{
                  height: 34,
                  width: 34,
                  border: "1px solid rgba(226, 232, 240, 0.8)",
                  borderRadius: "6px",
                  backgroundColor: "rgba(255,255,255,0.5)",
                }}
              >
                <FileDownloadOutlinedIcon
                  fontSize="small"
                  sx={{ color: "#64748b" }}
                />
              </IconButton>
            </Tooltip>
            <Tooltip title="View Settings">
              <IconButton
                size="small"
                onClick={(e) => setSettingsAnchor(e.currentTarget)}
                sx={{
                  height: 34,
                  width: 34,
                  border: "1px solid rgba(226, 232, 240, 0.8)",
                  borderRadius: "6px",
                  backgroundColor: settingsAnchor
                    ? "#f1f5f9"
                    : "rgba(255,255,255,0.5)",
                }}
              >
                <SettingsOutlinedIcon
                  fontSize="small"
                  sx={{ color: "#64748b" }}
                />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Bulk Action Bar */}
        {selected.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              px: 3,
              py: 1.5,
              backgroundColor: "#1a203e",
              color: "#fff",
              animation: "slideDown 0.3s ease-out",
              "@keyframes slideDown": {
                from: { height: 0, opacity: 0 },
                to: { height: "auto", opacity: 1 },
              },
            }}
          >
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
              {selected.length} campaigns selected
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={handleDownload}
              sx={{
                color: "#fff",
                borderColor: "rgba(255,255,255,0.3)",
                textTransform: "none",
                fontSize: "0.75rem",
                fontWeight: 600,
              }}
            >
              Export Selection
            </Button>
            <Button
              size="small"
              onClick={() => setSelected([])}
              sx={{
                ml: "auto",
                color: "rgba(255,255,255,0.6)",
                textTransform: "none",
                fontSize: "0.75rem",
              }}
            >
              Deselect All
            </Button>
          </Box>
        )}

        {/* Table */}
        <TableContainer>
          <Table stickyHeader sx={{ minWidth: 800 }}>
            <TableHead sx={tableHeaderSX}>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allPageSelected}
                    indeterminate={somePageSelected}
                    onChange={toggleAll}
                    sx={{ color: "white", "&.Mui-checked": { color: "white" } }}
                  />
                </TableCell>
                <TableCell>Campaign / Subject</TableCell>
                {ALL_COLUMNS.filter((c) => visibleCols.includes(c.key)).map(
                  (col) => (
                    <TableCell
                      key={col.key}
                      align={col.key === "actions" ? "center" : "left"}
                    >
                      {col.label}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {!hasData ? (
                <TableRow>
                  <TableCell
                    colSpan={visibleCols.length + 2}
                    align="center"
                    sx={{ py: 10 }}
                  >
                    <Typography
                      sx={{
                        color: "#94a3b8",
                        fontSize: "0.9rem",
                        fontWeight: 500,
                      }}
                    >
                      Click the <strong>Search</strong> button to load campaigns.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : paginated.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={visibleCols.length + 2}
                    align="center"
                    sx={{ py: 10 }}
                  >
                    <Typography
                      sx={{
                        color: "#94a3b8",
                        fontSize: "0.9rem",
                        fontWeight: 500,
                      }}
                    >
                      No campaigns found matching your criteria.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginated.map((row) => {
                  const sc = STATUS_COLORS[row.status] || STATUS_COLORS.DRAFT;
                  const isSelected = selected.includes(row.id);
                  return (
                    <TableRow
                      key={row.id}
                      hover
                      selected={isSelected}
                      sx={{
                        transition: "background-color 0.2s",
                        "&.Mui-selected": {
                          backgroundColor:
                            "rgba(59, 130, 246, 0.05) !important",
                        },
                        "&:hover": {
                          backgroundColor: "rgba(248, 250, 252, 0.8) !important",
                        },
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={isSelected}
                          onChange={() => toggleRow(row.id)}
                          sx={{
                            color: "#cbd5e1",
                            "&.Mui-checked": { color: "#1a203e" },
                          }}
                        />
                      </TableCell>

                      <TableCell sx={cellSX}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: "8px",
                              backgroundColor: "rgba(59, 130, 246, 0.1)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <EmailOutlinedIcon
                              sx={{ color: "#3b82f6", fontSize: 18 }}
                            />
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: "#1e293b",
                                fontSize: "0.85rem",
                                lineHeight: 1.2,
                              }}
                            >
                              {row.name}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mt: 0.5,
                              }}
                            >
                              <Typography
                                sx={{
                                  color: "#94a3b8",
                                  fontSize: "0.7rem",
                                  fontWeight: 600,
                                }}
                              >
                                ID: {row.id}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </TableCell>

                      {visibleCols.includes("templateName") && (
                        <TableCell sx={cellSX}>{row.templateName}</TableCell>
                      )}

                      {visibleCols.includes("emailType") && (
                        <TableCell sx={cellSX}>
                          <Chip
                            label={row.emailType}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: "0.65rem",
                              fontWeight: 600,
                              height: 20,
                              color: "#64748b",
                              borderColor: "#e2e8f0",
                            }}
                          />
                        </TableCell>
                      )}

                      {visibleCols.includes("logID") && (
                        <TableCell sx={cellSX}>{row.logID}</TableCell>
                      )}

                      {visibleCols.includes("sentOn") && (
                        <TableCell sx={cellSX}>{row.sentOn || "—"}</TableCell>
                      )}

                      {visibleCols.includes("status") && (
                        <TableCell sx={cellSX}>
                          <Box
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              borderRadius: "6px",
                              bgcolor: sc.bg,
                              color: sc.color,
                              fontSize: "0.7rem",
                              fontWeight: 700,
                              textAlign: "center",
                              minWidth: 80,
                              display: "inline-block",
                            }}
                          >
                            {row.status}
                          </Box>
                        </TableCell>
                      )}

                      {visibleCols.includes("actions") && (
                        <TableCell sx={cellSX} align="center">
                          <IconButton
                            size="small"
                            onClick={() => setSelectedLog(row)}
                            sx={{
                              border: "1px solid #e2e8f0",
                              borderRadius: "8px",
                              backgroundColor:
                                selectedLog?.id === row.id
                                  ? "#1a203e"
                                  : "transparent",
                              color:
                                selectedLog?.id === row.id ? "#fff" : "#64748b",
                              "&:hover": {
                                backgroundColor: "#1a203e",
                                color: "#fff",
                              },
                              transition: "all 0.2s",
                            }}
                          >
                            <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 1.5,
            borderTop: "1px solid rgba(226, 232, 240, 0.8)",
            backgroundColor: "rgba(248, 250, 252, 0.5)",
          }}
        >
          <Typography
            sx={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 600 }}
          >
            {!hasData
              ? "No data loaded"
              : filtered.length === 0
                ? "Showing 0 campaigns"
                : `Showing ${page * rowsPerPage + 1}–${Math.min(
                  (page + 1) * rowsPerPage,
                  filtered.length
                )} of ${filtered.length} entries`}
          </Typography>
          {hasData && (
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                border: "none",
                "& .MuiToolbar-root": { minHeight: 32, p: 0 },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                {
                  fontSize: "0.75rem",
                  color: "#64748b",
                  fontWeight: 600,
                },
                "& .MuiTablePagination-select": {
                  fontSize: "0.75rem",
                  fontWeight: 700,
                },
                "& .MuiTablePagination-actions .MuiIconButton-root": {
                  p: 0.5,
                  color: "#1a203e",
                },
              }}
            />
          )}
        </Box>
      </Paper>

      {/* Log Detail Dialog */}
      <Dialog
        open={Boolean(selectedLog)}
        onClose={() => setSelectedLog(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px", ...glassSx } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            fontSize: "1.1rem",
            color: "#1a203e",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 2,
          }}
        >
          Campaign Report Details
          <IconButton
            onClick={() => setSelectedLog(null)}
            size="small"
            sx={{ bgcolor: "#f1f5f9", "&:hover": { bgcolor: "#e2e8f0" } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {selectedLog && (
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {[
                {
                  label: "Subject",
                  value: selectedLog.name,
                  icon: <EmailOutlinedIcon fontSize="small" />,
                },
                {
                  label: "Template",
                  value: selectedLog.templateName,
                  icon: <SettingsOutlinedIcon fontSize="small" />,
                },
                {
                  label: "Log ID",
                  value: selectedLog.logID,
                  icon: <InfoOutlinedIcon fontSize="small" />,
                },
                {
                  label: "Sent Date",
                  value: selectedLog.sentOn || "NA",
                  icon: <VisibilityOutlinedIcon fontSize="small" />,
                },
              ].map((item) => (
                <Grid item xs={12} sm={6} md={3} key={item.label}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "12px",
                      border: "1px solid #e2e8f0",
                      height: "100%",
                      bgcolor: "rgba(255,255,255,0.4)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      {React.cloneElement(item.icon, {
                        sx: { fontSize: 14, color: "#3b82f6" },
                      })}
                      <Typography
                        sx={{
                          fontSize: "0.65rem",
                          fontWeight: 800,
                          color: "#64748b",
                          textTransform: "uppercase",
                        }}
                      >
                        {item.label}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "0.85rem",
                        fontWeight: 700,
                        color: "#1e293b",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item.value}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}

          <Box sx={{ mt: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                color: "#1a203e",
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              Detailed Logs
              <Chip
                label={emailLogs?.data?.length || 0}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.7rem",
                  fontWeight: 800,
                  bgcolor: "#1a203e",
                  color: "#fff",
                }}
              />
            </Typography>

            {emailLogsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress
                  size={36}
                  thickness={4}
                  sx={{ color: "#1a203e" }}
                />
              </Box>
            ) : emailLogsError ? (
              <Alert severity="error" sx={{ borderRadius: "12px" }}>
                Failed to load report data. Please try again.
              </Alert>
            ) : emailLogs?.data?.length > 0 ? (
              <TableContainer
                sx={{ borderRadius: "12px", border: "1px solid #e2e8f0" }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f8fafc" }}>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          color: "#475569",
                          fontSize: "0.75rem",
                        }}
                      >
                        Email Recipient
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          color: "#475569",
                          fontSize: "0.75rem",
                        }}
                      >
                        Status
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          color: "#475569",
                          fontSize: "0.75rem",
                        }}
                      >
                        Response Details
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 700,
                          color: "#475569",
                          fontSize: "0.75rem",
                        }}
                      >
                        Timestamp
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {emailLogs.data.map((entry) => (
                      <TableRow key={entry.email_report_id} hover>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "#1e293b",
                            fontSize: "0.8rem",
                          }}
                        >
                          {entry.email_address}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={entry.send_status}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.65rem",
                              fontWeight: 700,
                              bgcolor:
                                entry.send_status === "sent"
                                  ? "#dcfce7"
                                  : "#fee2e2",
                              color:
                                entry.send_status === "sent"
                                  ? "#15803d"
                                  : "#b91c1c",
                              textTransform: "uppercase",
                            }}
                          />
                        </TableCell>
                        <TableCell
                          sx={{ fontSize: "0.75rem", color: "#64748b" }}
                        >
                          {entry.res_server || "Success"}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontSize: "0.75rem", color: "#94a3b8" }}
                        >
                          {new Date(entry.createdAt).toLocaleString("en-US", {
                            month: "short",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box
                sx={{
                  textAlign: "center",
                  py: 6,
                  bgcolor: "#f8fafc",
                  borderRadius: "12px",
                }}
              >
                <Typography sx={{ color: "#94a3b8", fontSize: "0.85rem" }}>
                  No detailed records found.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Column Settings Menu */}
      <Menu
        anchorEl={settingsAnchor}
        open={Boolean(settingsAnchor)}
        onClose={() => setSettingsAnchor(null)}
        PaperProps={{
          sx: {
            minWidth: 220,
            borderRadius: "12px",
            mt: 1,
            boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
            ...glassSx,
          },
        }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 800,
              color: "#1e293b",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Column Settings
          </Typography>
        </Box>
        {ALL_COLUMNS.map((col) => (
          <MenuItem
            key={col.key}
            dense
            onClick={() => toggleCol(col.key)}
            sx={{ py: 1, px: 2 }}
          >
            <Checkbox
              size="small"
              checked={visibleCols.includes(col.key)}
              sx={{ p: 0, mr: 1.5, color: "#cbd5e1" }}
            />
            <Typography
              sx={{ fontSize: "0.85rem", fontWeight: 500, color: "#475569" }}
            >
              {col.label}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default EmailCampaignReport;